/* eslint-disable no-console */
/* eslint-disable no-plusplus */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { LoadingOutlined } from "@ant-design/icons";
import { MAX_BOUNDS } from "../../Home/Data/HomeData";
import { renderCurrentPoint, renderPolygonPoint } from "./renderIcons";
import RestartModal from "./RestartModal";
import SuccessModal from "./SuccessModal";

const DEFAULT_CENTRE = [-0.6803741, 34.7569482];

const MarkPointScreen = () => {
  const { id, coords } = useParams();

  const [currentSession, setCurrentSession] = useState([]);
  const [restartModal, setRestartModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [loading, setLoading] = useState({
    mark: false,
    reset: false,
    complete: false,
  });

  const mapRef = useRef(null);

  const getCoordinates = () => {
    if (coords) {
      const [lat, long] = coords.split("+");
      return [parseFloat(lat), parseFloat(long)];
    }

    return DEFAULT_CENTRE;
  };

  const getSessionById = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/session/session-details`,
        {
          params: {
            sessionId: +id,
          },
        },
      );

      setCurrentSession(response.data.session);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const handleReset = async () => {
    try {
      setLoading((prev) => ({ ...prev, reset: true }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/session/clear-area-coords`,
        {
          sessionId: +id,
        },
      );

      await getSessionById();

      const deeplink = "myapp://sessions/measure-land";
      window.location.href = deeplink;
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((prev) => ({ ...prev, reset: false }));
    }
  };

  const handleSave = async () => {
    const payload = {
      sessionId: +id,
      coords: getCoordinates(),
    };

    try {
      setLoading((prev) => ({ ...prev, mark: true }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/session/add-area-coords`,
        payload,
      );

      const deeplink = "myapp://sessions/measure-land";
      window.location.href = deeplink;
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((prev) => ({ ...prev, mark: false }));
    }
  };

  const handleComplete = async () => {
    const payload = {
      sessionId: +id,
    };

    try {
      setLoading((prev) => ({ ...prev, complete: true }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/session/complete-and-calculate-area`,
        payload,
      );

      setSuccessModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((prev) => ({ ...prev, complete: false }));
    }
  };

  // to load the map correctly
  useEffect(() => {
    const timeout = setTimeout(() => {
      mapRef?.current?.invalidateSize(true);
    }, 200);

    return () => clearTimeout(timeout);
  });

  // get session details
  useEffect(() => {
    if (id) {
      getSessionById();
    }
  }, [id, getSessionById]);

  return (
    <div className="relative w-screen" style={{ height: "100dvh" }}>
      {currentSession?.areaCoords?.length >= 1 ? (
        <button
          type="button"
          className="absolute top-4 left-4 z-[999] flex-1 flex items-center justify-center py-2.75 px-3 h-10 bg-alert-10 rounded-md"
          style={{
            boxShadow: "2px 2px 8px 0px #0000001A",
          }}
          onClick={(e) => {
            e.preventDefault();
            setRestartModal(true);
          }}
          disabled={loading.reset}
        >
          <p className="text-alert-red font-poppins text-xs font-medium">
            Restart
          </p>

          {loading.reset && (
            <LoadingOutlined size="sm" className="text-black ml-2" />
          )}
        </button>
      ) : null}

      <div
        className="absolute bottom-0 rounded-t-2xl bg-white z-[999] flex items-center justify-between pt-4 px-4 pb-6 w-full"
        style={{
          boxShadow: " 0px 1px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <div className="flex items-center gap-x-3 w-full">
          <button
            type="button"
            disabled={loading.mark}
            className="flex-1 relative z-[9999] flex items-center justify-center py-2.75 px-3 h-10 bg-primary rounded-md"
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <p className="font-poppins text-xs font-medium text-background-white">
              Mark Point
            </p>

            {loading.mark && (
              <LoadingOutlined size="sm" className="text-white ml-2" />
            )}
          </button>

          {currentSession?.areaCoords?.length >= 2 ? (
            <button
              type="button"
              disabled={loading.complete}
              className="flex-1 relative z-[9999] flex items-center justify-center py-2.75 px-3 h-10 rounded-md"
              style={{
                backgroundColor: "#FAD152",
                color: "#000000",
              }}
              onClick={(e) => {
                e.preventDefault();
                handleComplete();
              }}
            >
              <p className="font-poppins text-xs font-medium">Complete</p>

              {loading.complete && (
                <LoadingOutlined size="sm" className="text-white ml-2" />
              )}
            </button>
          ) : (
            <div
              className="flex-1 relative z-[9999] py-2.75 px-3 h-10 rounded-md"
              style={{
                backgroundColor: "#E7E7E7",
                color: "#B4B4B4",
              }}
            >
              <p className="font-poppins text-xs font-medium text-center">
                Complete
              </p>
            </div>
          )}
        </div>
      </div>

      <MapContainer
        center={getCoordinates()}
        zoom={20} // max value
        maxBounds={MAX_BOUNDS}
        className="z-10 w-full h-full"
        zoomControl={false}
        ref={mapRef}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {currentSession?.areaCoords?.length
          ? currentSession?.areaCoords.map((item) => (
              <Marker
                key={`${item}`}
                position={item}
                icon={renderPolygonPoint()}
              />
            ))
          : null}

        <Marker
          key={`${getCoordinates()}`}
          position={getCoordinates()}
          icon={renderCurrentPoint()}
        />
      </MapContainer>

      <RestartModal
        visible={restartModal}
        onCancel={() => setRestartModal(false)}
        onRestart={handleReset}
      />

      <SuccessModal
        visible={successModal}
        onCancel={() => setSuccessModal(false)}
      />
    </div>
  );
};

export default MarkPointScreen;
